import { useContext, useMemo, useRef, useEffect, useCallback } from 'react';
import { EventBusContext, useTraitScopeChain } from '@almadar/ui/providers';
import { isSystemUiEvent } from '@almadar/core';
import { createLogger } from '@almadar/logger';

var log = createLogger("almadar:eventbus");
var subLog = createLogger("almadar:eventbus:subscribe");
var scopeLog = createLogger("almadar:ui:trait-scope");
function setGlobalEventBus(bus) {
  if (typeof window !== "undefined") {
    window.__kflowEventBus = bus;
  }
}
function getGlobalEventBus() {
  if (typeof window !== "undefined") {
    return window.__kflowEventBus ?? null;
  }
  return null;
}
var fallbackListeners = /* @__PURE__ */ new Map();
var fallbackAnyListeners = /* @__PURE__ */ new Set();
var fallbackEventBus = {
  emit: (type, payload, source) => {
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      source
    };
    const handlers = fallbackListeners.get(type);
    log.debug("emit", { type, payloadKeys: payload ? Object.keys(payload).length : 0, listenerCount: (handlers?.size ?? 0) + fallbackAnyListeners.size });
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          log.error("Error in listener", { type, error: error instanceof Error ? error : String(error) });
        }
      });
    }
    fallbackAnyListeners.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        log.error("Error in onAny listener", { type, error: error instanceof Error ? error : String(error) });
      }
    });
  },
  on: (type, listener) => {
    if (!fallbackListeners.has(type)) {
      fallbackListeners.set(type, /* @__PURE__ */ new Set());
    }
    fallbackListeners.get(type).add(listener);
    subLog.debug("subscribe", { type, totalListeners: fallbackListeners.get(type).size });
    return () => {
      const handlers = fallbackListeners.get(type);
      if (handlers) {
        handlers.delete(listener);
        if (handlers.size === 0) {
          fallbackListeners.delete(type);
        }
      }
    };
  },
  once: (type, listener) => {
    const wrappedListener = (event) => {
      fallbackListeners.get(type)?.delete(wrappedListener);
      listener(event);
    };
    return fallbackEventBus.on(type, wrappedListener);
  },
  hasListeners: (type) => {
    const handlers = fallbackListeners.get(type);
    return handlers !== void 0 && handlers.size > 0;
  },
  onAny: (listener) => {
    fallbackAnyListeners.add(listener);
    subLog.debug("subscribe:any", { totalAnyListeners: fallbackAnyListeners.size });
    return () => {
      fallbackAnyListeners.delete(listener);
    };
  }
};
function useEventBus() {
  const context = useContext(EventBusContext);
  const baseBus = context ?? getGlobalEventBus() ?? fallbackEventBus;
  const chain = useTraitScopeChain();
  return useMemo(() => {
    if (chain.length === 0) {
      return {
        ...baseBus,
        emit: (type, payload, source) => {
          if (typeof type === "string" && type.startsWith("UI:") && !type.slice(3).includes(".") && !isSystemUiEvent(type.slice(3)) && !source?.trait) {
            scopeLog.warn("emit:bare-key-no-scope", { type });
          }
          baseBus.emit(type, payload, source);
        }
      };
    }
    return {
      ...baseBus,
      emit: (type, payload, source) => {
        if (typeof type === "string" && type.startsWith("UI:") && !isSystemUiEvent(type.slice(3))) {
          const tail = type.slice(3);
          const isQualified = tail.includes(".");
          const event = isQualified ? tail.split(".").slice(2).join(".") : tail;
          if (!event) {
            baseBus.emit(type, payload, source);
            return;
          }
          const keys = /* @__PURE__ */ new Set();
          if (isQualified) keys.add(type);
          for (const sc of chain) {
            keys.add(`UI:${sc.orbital}.${sc.trait}.${event}`);
          }
          if (keys.size > 1) {
            scopeLog.info("emit:fan-out", {
              from: type,
              keys: Array.from(keys),
              chainDepth: chain.length
            });
          }
          const physical = keys.size > 1 ? source ?? {} : source;
          for (const key of keys) baseBus.emit(key, payload, physical);
          return;
        }
        baseBus.emit(type, payload, source);
      }
    };
  }, [baseBus, chain]);
}
function useEventListener(event, handler) {
  const eventBus = useEventBus();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    const wrappedHandler = (evt) => {
      handlerRef.current(evt);
    };
    const unsub = eventBus.on(event, wrappedHandler);
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [event, eventBus]);
}
var useEventSubscription = useEventListener;
function useEmitEvent() {
  const eventBus = useEventBus();
  return useCallback(
    (type, payload, source) => {
      eventBus.emit(type, payload, source);
    },
    [eventBus]
  );
}
var useEventBus_default = useEventBus;

export { getGlobalEventBus, setGlobalEventBus, useEmitEvent, useEventBus, useEventBus_default, useEventListener, useEventSubscription };
